/**
 * Render scale and automatic lowering (W2-01a preview-gl; ARCHITECTURE.md §7.3, DESIGN.md D19; W3-03 UX-028).
 *
 * The backing store of the preview canvas is css size x device-pixel ratio x a factor. The device-pixel ratio is the
 * caller's (capped by the "Render resolution" setting); the factor is 1 for a fixed setting and adapts in the
 * automatic mode, for moving frames only:
 *   - lower: when the frame work (CPU time of `render` plus the GPU time where the browser exposes a timer) stays
 *     above 14 ms for 1 s of continuous frames, the factor drops one step;
 *   - verify: the next second of frames must show the saving a smaller canvas brings (the work falls by at least half
 *     of what the area ratio predicts, and frames slower than the display come at least that much faster);
 *     otherwise the frame is not fill-bound (CPU-bound, or held by something else, like a software GPU whose cost is
 *     per frame), the drop is undone and the governor stops lowering for a while (VERDICT_MS, or until the canvas
 *     size or the mode changes), so a slow machine is not left with a blurry preview that is just as slow;
 *   - raise: one step after 5 s of frames whose predicted cost at the higher resolution (work x area ratio) stays
 *     under 10 ms, never sooner than 10 s after a drop (no oscillation);
 *   - still frames (paused editing: the chart time did not move since the last frame) always draw at full
 *     resolution: the factor resets before such a frame (`beforeFrame`), so a stopped preview never stays blurry.
 * The frames right after a size change are not sampled: resizing a WebGL canvas makes the browser wait for the GPU,
 * and that one-off stall must not count as the frame's work.
 */

export type RenderScaleMode = 'auto' | 'fixed';

/** Factors the automatic mode steps through. */
export const RENDER_SCALE_STEPS: readonly number[] = [1, 0.85, 0.7, 0.5];
/** A frame counts as slow above this work time (ms). */
export const SLOW_FRAME_MS = 14;
/** Slow frames must persist this long (ms) before the factor drops. */
export const SLOW_WINDOW_MS = 1000;
/** Frames further apart than this (ms) are not continuous (paused editing): the windows restart. */
export const FRAME_GAP_MS = 250;
/** Predicted work under which the factor may rise (ms), for this long (ms), not sooner than RAISE_COOLDOWN_MS after a drop. */
export const FAST_FRAME_MS = 10;
export const FAST_WINDOW_MS = 5000;
export const RAISE_COOLDOWN_MS = 10000;
/** Frames measured after a drop to check it helped (ms of continuous frames). */
export const VERIFY_WINDOW_MS = 1000;
/** A drop must remove at least this share of the saving the area ratio predicts, else it is undone. */
export const VERIFY_MIN_SHARE = 0.5;
/** How long a "lowering does not help" verdict holds (ms) unless the canvas size or the mode changes first. */
export const VERDICT_MS = 60_000;
/** Frames not sampled after a size change (the resize's synchronous GPU stall). */
export const SETTLE_FRAMES = 3;
/** Frame intervals up to this (ms) count as display-bound: a drop is then judged by the work alone. */
export const VSYNC_BOUND_MS = 20;

interface Verify {
  /** Step before the drop. */
  readonly from: number;
  /** Mean work and mean frame interval over the slow window that caused the drop. */
  readonly before: number;
  readonly intervalBefore: number;
  since: number;
  sum: number;
  gaps: number;
  n: number;
}

export class RenderScaleGovernor {
  private modeValue: RenderScaleMode = 'auto';
  private step = 0;
  private slowSince = NaN;
  private slowSum = 0;
  private slowGaps = 0;
  private slowCount = 0;
  private fastSince = NaN;
  private lastFrame = NaN;
  private lastChange = -Infinity;
  private verify: Verify | null = null;
  /** Lowering was shown not to help until this time (ms). */
  private noLowerUntil = -Infinity;
  private settle = 0;

  get mode(): RenderScaleMode {
    return this.modeValue;
  }

  /** Current factor (1 in the fixed mode). */
  get factor(): number {
    return this.modeValue === 'fixed' ? 1 : (RENDER_SCALE_STEPS[this.step] ?? 1);
  }

  /** True while a "lowering does not help" verdict holds at `nowMs`. */
  lowersBlocked(nowMs: number): boolean {
    return nowMs < this.noLowerUntil;
  }

  setMode(mode: RenderScaleMode): boolean {
    if (this.modeValue === mode) return false;
    const before = this.factor;
    this.modeValue = mode;
    this.step = 0;
    this.verify = null;
    this.noLowerUntil = -Infinity;
    this.reset();
    return this.factor !== before;
  }

  /** The canvas size changed: forget the verdict (another size may be fill-bound) and skip the stall frames. */
  noteResize(cssChanged: boolean): void {
    this.settle = SETTLE_FRAMES;
    if (cssChanged) this.noLowerUntil = -Infinity;
  }

  private reset(): void {
    this.slowSince = NaN;
    this.slowSum = 0;
    this.slowGaps = 0;
    this.slowCount = 0;
    this.fastSince = NaN;
  }

  /**
   * Before drawing a frame: a still frame (the chart time did not move) draws at full resolution. Returns true when
   * the factor changed (the canvas must be resized before drawing).
   */
  beforeFrame(still: boolean, nowMs: number): boolean {
    if (this.modeValue === 'fixed' || !still || this.step === 0) return false;
    this.step = 0;
    this.verify = null;
    this.lastChange = nowMs;
    this.reset();
    return true;
  }

  /** Records one frame's work; returns true when the factor changed (the canvas must be resized). */
  sample(workMs: number, nowMs: number): boolean {
    if (this.modeValue === 'fixed' || !Number.isFinite(workMs)) return false;
    const gap = nowMs - this.lastFrame;
    this.lastFrame = nowMs;
    if (this.settle > 0) {
      this.settle--;
      return false;
    }
    if (!(gap <= FRAME_GAP_MS)) {
      this.reset();
      if (this.verify !== null) this.verify.since = nowMs;
      return false;
    }
    const v = this.verify;
    if (v !== null) return this.verifySample(v, workMs, nowMs, gap);
    if (workMs > SLOW_FRAME_MS) {
      this.fastSince = NaN;
      if (Number.isNaN(this.slowSince)) this.slowSince = nowMs - gap;
      this.slowSum += workMs;
      this.slowGaps += gap;
      this.slowCount++;
      if (
        nowMs - this.slowSince >= SLOW_WINDOW_MS &&
        this.step < RENDER_SCALE_STEPS.length - 1 &&
        !this.lowersBlocked(nowMs)
      ) {
        this.verify = {
          from: this.step,
          before: this.slowSum / Math.max(1, this.slowCount),
          intervalBefore: this.slowGaps / Math.max(1, this.slowCount),
          since: nowMs,
          sum: 0,
          gaps: 0,
          n: 0,
        };
        this.step++;
        this.lastChange = nowMs;
        this.reset();
        return true;
      }
      return false;
    }
    this.slowSince = NaN;
    this.slowSum = 0;
    this.slowGaps = 0;
    this.slowCount = 0;
    if (this.step === 0) return false;
    const cur = RENDER_SCALE_STEPS[this.step] ?? 1;
    const up = RENDER_SCALE_STEPS[this.step - 1] ?? 1;
    const predicted = workMs * (up / cur) ** 2;
    if (predicted >= FAST_FRAME_MS) {
      this.fastSince = NaN;
      return false;
    }
    if (Number.isNaN(this.fastSince)) this.fastSince = nowMs - gap;
    if (nowMs - this.fastSince >= FAST_WINDOW_MS && nowMs - this.lastChange >= RAISE_COOLDOWN_MS) {
      this.step--;
      this.lastChange = nowMs;
      this.reset();
      return true;
    }
    return false;
  }

  /** A frame measured after a drop; at the end of the window the drop is kept or undone. */
  private verifySample(v: Verify, workMs: number, nowMs: number, gap: number): boolean {
    if (v.n === 0) v.since = nowMs - gap;
    v.sum += workMs;
    v.gaps += gap;
    v.n++;
    if (nowMs - v.since < VERIFY_WINDOW_MS) return false;
    this.verify = null;
    const after = v.sum / v.n;
    const ratio = ((RENDER_SCALE_STEPS[this.step] ?? 1) / (RENDER_SCALE_STEPS[v.from] ?? 1)) ** 2;
    const saving = v.before * (1 - ratio);
    const cheaper = v.before - after >= saving * VERIFY_MIN_SHARE;
    // Frames slower than the display: the frames themselves must come faster too (the preview was what held them).
    const faster = v.intervalBefore <= VSYNC_BOUND_MS || v.intervalBefore - v.gaps / v.n >= saving * VERIFY_MIN_SHARE;
    if (cheaper && faster) return false;
    // Not fill-bound: back to the previous resolution, and no more lowering for a while.
    this.step = v.from;
    this.lastChange = nowMs;
    this.noLowerUntil = nowMs + VERDICT_MS;
    this.reset();
    return true;
  }
}

/** Backing-store size of a canvas: css x dpr x factor, at least 1 x 1. */
export function backingSize(cssW: number, cssH: number, dpr: number, factor: number): readonly [number, number] {
  const k = (dpr > 0 && Number.isFinite(dpr) ? dpr : 1) * (factor > 0 ? factor : 1);
  const w = Math.max(1, Math.round((cssW > 0 && Number.isFinite(cssW) ? cssW : 1) * k));
  const h = Math.max(1, Math.round((cssH > 0 && Number.isFinite(cssH) ? cssH : 1) * k));
  return [w, h];
}
